import type { AxiosRequestConfig } from '@ahm/api-wrappers-core';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { apiRoutes } from './api-routes';
import type { Progress } from './use-get-deal-progress';
import { webviewHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';
import { createDealQueryKey } from '@/lib/query-key-utils';
import { CACHE_STRATEGIES } from '@/lib/react-query/query-client';

/**
 * Flash Deal item (API v4). Item shape trùng với `Progress` của v3
 * (/promo/progress) — tái dùng type để 1 nguồn contract, tránh drift.
 * Khác biệt v4: response là MẢNG TRẦN (không còn envelope { progress: [] }),
 * lọc bằng `is_scheme`, không còn skip/reach_max_use.
 */
export type FlashDealItem = Progress;

export interface FlashDealsParams {
  city_id: string;
  /** min=1. Home strip = 10, trang detail = 200 (không infinite scroll). */
  limit: number;
  lat?: number;
  lng?: number;
  /** true = chỉ deal độc quyền; false = chỉ deal thường (loại độc quyền). */
  is_scheme?: boolean;
}

export const getFlashDealsFn = async (
  params: FlashDealsParams,
  config: AxiosRequestConfig = {}
): Promise<FlashDealItem[]> => {
  const response = await webviewHttpClient.get<FlashDealItem[]>(
    apiRoutes.ahafood.web_view.get_flash_deals,
    {
      ...config,
      params: {
        city_id: params.city_id,
        limit: params.limit,
        ...(params.is_scheme !== undefined && { is_scheme: params.is_scheme }),
        ...(params.lat &&
          params.lng && {
            lat: params.lat,
            lng: params.lng,
          }),
      },
    }
  );

  // API trả mảng trần — phòng thủ nếu BE trả null/không phải mảng.
  return Array.isArray(response?.data) ? response.data : [];
};

export const useGetFlashDeals = (
  params: FlashDealsParams,
  opts?: Omit<UseQueryOptions<FlashDealItem[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<FlashDealItem[], Error>({
    // Normalized geohash key (~4.9km) — is_scheme/limit nằm trong params nên key tự phân biệt.
    queryKey: createDealQueryKey(params),
    queryFn: () => getFlashDealsFn(params), // Vẫn gửi lat/lng chính xác cho API
    ...CACHE_STRATEGIES.realtime, // 1min cache - flash deal đổi liên tục
    ...opts,
    // city_id đến bất đồng bộ (by-point) nên luôn có cửa sổ nó còn rỗng. Bắn
    // `flash-deals?city_id=` sẽ lỗi — gate như useGetStores/useGetDealProgress.
    enabled: Boolean(params.city_id) && (opts?.enabled ?? true),
  });
};

export interface FlashDealsWithFallback {
  deals: FlashDealItem[];
  /** true nếu danh sách đang là deal độc quyền; false nếu đã fallback deal thường. */
  showedExclusive: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Home strip: ưu tiên deal độc quyền (is_scheme=true). Nếu rỗng → fallback
 * deal thường (is_scheme=false). Dependent query — query 2 chỉ chạy khi
 * query 1 đã fetched & rỗng.
 */
export const useFlashDealsWithFallback = (params: {
  city_id: string;
  limit: number;
  lat?: number;
  lng?: number;
}): FlashDealsWithFallback => {
  const exclusive = useGetFlashDeals({ ...params, is_scheme: true });

  const exclusiveEmpty =
    exclusive.isSuccess && (exclusive.data?.length ?? 0) === 0;

  const normal = useGetFlashDeals(
    { ...params, is_scheme: false },
    { enabled: exclusiveEmpty }
  );

  const showedExclusive = (exclusive.data?.length ?? 0) > 0;
  const deals = showedExclusive ? (exclusive.data ?? []) : (normal.data ?? []);

  return {
    deals,
    showedExclusive,
    isLoading: exclusive.isLoading || (exclusiveEmpty && normal.isLoading),
    isError: exclusive.isError || (exclusiveEmpty && normal.isError),
  };
};
